import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import ChatWidget from './study-session/ChatWidget';
import { feedbackApi } from '../services/api';
import type { ChatMode } from '../services/api';
import { analytics } from '../services/analytics';
import { MixedText } from '../utils/rtl';

type ChatMessage = { type: 'user' | 'ai'; message: string };

interface FeedbackChatWidgetProps {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
}

const FeedbackChatWidget: React.FC<FeedbackChatWidgetProps> = ({
  isOpen,
  onClose,
  onOpen
}) => {
  const { t, i18n } = useTranslation();
  const effectiveRTL = i18n.language === 'he';

  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [sessionKey, setSessionKey] = useState<string | undefined>();
  const [isCompleted, setIsCompleted] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);

  const sessionKeyRef = useRef<string | undefined>();
  const closeTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    sessionKeyRef.current = sessionKey;
  }, [sessionKey]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    setChatHistory([]);
    setSessionKey(undefined);
    sessionKeyRef.current = undefined;
    setIsCompleted(false);
    setMessageCount(0);
    setIsMinimized(false);

    onClose();
  }, [onClose]);

  const handleChatUpdate = useCallback((message: ChatMessage) => {
    setChatHistory((prev) => [...prev, message]);
  }, []);

  const handleFeedbackChat = useCallback(async (message: string, _mode?: ChatMode) => {
    analytics.trackFeedbackMessage(message, true, sessionKeyRef.current);

    try {
      const response = await feedbackApi.chatWithFeedback(message, sessionKeyRef.current);
      const resolvedSessionKey = response.session_key ?? sessionKeyRef.current;

      if (response.session_key && response.session_key !== sessionKeyRef.current) {
        sessionKeyRef.current = response.session_key;
        setSessionKey(response.session_key);
      }

      analytics.trackFeedbackMessage(response.response, false, resolvedSessionKey);

      const newCount = messageCount + 2;
      setMessageCount(newCount);

      if (response.completed) {
        setIsCompleted(true);
        analytics.trackFeedbackSessionCompleted(newCount, resolvedSessionKey);

        if (closeTimeoutRef.current) {
          window.clearTimeout(closeTimeoutRef.current);
        }

        closeTimeoutRef.current = window.setTimeout(() => {
          handleClose();
        }, 3000);
      }

      return { response: response.response };
    } catch (error) {
      console.error('Error sending feedback message:', error);
      return {
        response: t(
          'feedback:errorMessage',
          "Oops! I'm having trouble processing that. Could you try again? 🛸"
        )
      };
    }
  }, [handleClose, messageCount, t]);

  useEffect(() => {
    if (isOpen && chatHistory.length === 0) {
      analytics.trackFeedbackChatOpened();
      setChatHistory([
        {
          type: 'ai',
          message: t(
            'feedback:welcomeMessage',
            "Hey there! 👋 I'm Bob, your StudyBel astronaut guide! I'd love to hear your thoughts about StudyBel. What feedback would you like to share today?"
          )
        }
      ]);
    }
  }, [isOpen, chatHistory.length, t]);

  const assistantName = t('feedback:assistantName', 'Bob');
  const welcomeMessage = t(
    'feedback:welcomeMessage',
    "Hey there! 👋 I'm Bob, your StudyBel astronaut guide! I'd love to hear your thoughts about StudyBel. What feedback would you like to share today?"
  );
  const inputPlaceholder = t('feedback:placeholder', 'Tell us what you think...');

  const overlayContent = (
    <div className="absolute inset-0 bg-gradient-to-br from-vibrant-orange/95 to-muted-purple/95 z-50 flex items-center justify-center rounded-lg">
      <div className="text-center p-8">
        <div className="w-20 h-20 mx-auto mb-4">
          <img
            src="/astronaut_cool.png"
            alt="Success"
            className="w-full h-full object-contain"
          />
        </div>
        <h3 className="text-2xl font-bold text-white mb-2 font-hebrew">
          {t('feedback:submitted', 'Feedback Submitted!')}
        </h3>
        <p className="text-white/90 font-hebrew">
          {t('feedback:thankYou', 'Thank you for helping us improve StudyBel! 🚀')}
        </p>
      </div>
    </div>
  );

  if (!isOpen) {
    return createPortal(
      <button
        onClick={onOpen}
        className="fixed bottom-4 md:bottom-6 right-4 md:right-6 z-[60] bg-gradient-to-r from-vibrant-orange to-vibrant-orange/90 hover:from-vibrant-orange/90 hover:to-vibrant-orange/80 text-white px-3 md:px-4 py-2.5 md:py-3 rounded-full shadow-lg shadow-vibrant-orange/30 transition-all duration-200 hover:scale-105 flex items-center gap-2 md:gap-3"
        title={t('feedback:button', 'Got Feedback?')}
      >
        <span className="font-medium text-xs md:text-sm whitespace-nowrap hidden sm:inline">
          <MixedText>{t('feedback:button', 'Got Feedback?')}</MixedText>
        </span>
        <img
          src="/logo_v2.png"
          alt="Bob"
          className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-white/10 p-1"
        />
      </button>,
      document.body
    );
  }

  return createPortal(
    <ChatWidget
      sessionId={0}
      isOpen={isOpen}
      onClose={handleClose}
      chatHistory={chatHistory}
      onChatUpdate={handleChatUpdate}
      isRTL={effectiveRTL}
      customChatHandler={handleFeedbackChat}
      onMinimizedStateChange={setIsMinimized}
      assistantName={assistantName}
      assistantAvatarSrc="/logo_v2.png"
      welcomeMessage={welcomeMessage}
      inputPlaceholder={inputPlaceholder}
      overlayContent={overlayContent}
      isOverlayVisible={isCompleted && !isMinimized}
      isInputDisabled={isCompleted}
      enableModeSwitch={false}
    />,
    document.body
  );
};

export default FeedbackChatWidget;
